import React, { useState, useRef } from "react";
import { FiUpload, FiFile, FiCheck, FiX, FiAlertCircle, FiLoader, FiDownload, FiTable } from "react-icons/fi";
import toast from "react-hot-toast";
import Papa from "papaparse";

import Modal from "@/components/modal";
import { useComponentTranslation } from "@/services/i18n";
import { normalizeCountry } from "@/constants/countries";
import api from "@/services/api";

const translations = {
  en: {
    title: "Import Candidates",
    title_with_job: "Import Candidates to",
    tab_resume: "Resume (PDF)",
    tab_csv: "CSV Import",
    description_resume: "Upload resumes (PDF) to automatically extract candidate information and add them to your pool",
    description_resume_with_job: "Upload resumes (PDF) to automatically extract candidate information and add them to this job",
    description_csv: "Upload a CSV file with candidate information",
    description_csv_with_job: "Upload a CSV file with candidate information to add them to this job",
    drag_drop: "Drag and drop resumes here, or click to browse",
    drag_drop_csv: "Drag and drop a CSV file here, or click to browse",
    drag_drop_multiple: "You can select multiple files",
    files_selected: "files selected",
    candidates_found: "candidates found",
    add_more: "Add more files",
    remove: "Remove",
    supported_formats: "Supported formats: PDF",
    supported_formats_csv: "Supported format: CSV",
    cancel: "Cancel",
    import: "Import All",
    importing: "Importing...",
    success: "candidates imported successfully",
    error: "Error importing candidate",
    error_no_files: "Please select at least one file",
    error_no_candidates: "No valid candidates found in CSV",
    error_invalid_format: "Invalid file format. Only PDF files are allowed",
    error_invalid_csv: "Invalid file format. Only CSV files are allowed",
    error_missing_email: "Missing email",
    error_already_exists: "Already exists",
    status_pending: "Pending",
    status_uploading: "Uploading...",
    status_success: "Success",
    status_error: "Failed",
    import_complete: "Import complete",
    download_template: "Download CSV template",
    csv_columns: "Required: email. Optional: firstname, lastname, phone, city, country, linkedin",
  },
  fr: {
    title: "Importer des candidats",
    title_with_job: "Importer des candidats pour",
    tab_resume: "CV (PDF)",
    tab_csv: "Import CSV",
    description_resume: "Téléchargez des CV (PDF) pour extraire automatiquement les informations des candidats et les ajouter à votre vivier",
    description_resume_with_job: "Téléchargez des CV (PDF) pour extraire automatiquement les informations des candidats et les ajouter à cette offre",
    description_csv: "Téléchargez un fichier CSV avec les informations des candidats",
    description_csv_with_job: "Téléchargez un fichier CSV avec les informations des candidats pour les ajouter à cette offre",
    drag_drop: "Glissez-déposez des CV ici, ou cliquez pour parcourir",
    drag_drop_csv: "Glissez-déposez un fichier CSV ici, ou cliquez pour parcourir",
    drag_drop_multiple: "Vous pouvez sélectionner plusieurs fichiers",
    files_selected: "fichiers sélectionnés",
    candidates_found: "candidats trouvés",
    add_more: "Ajouter plus de fichiers",
    remove: "Supprimer",
    supported_formats: "Formats supportés : PDF",
    supported_formats_csv: "Format supporté : CSV",
    cancel: "Annuler",
    import: "Tout importer",
    importing: "Importation...",
    success: "candidats importés avec succès",
    error: "Erreur lors de l'importation du candidat",
    error_no_files: "Veuillez sélectionner au moins un fichier",
    error_no_candidates: "Aucun candidat valide trouvé dans le CSV",
    error_invalid_format: "Format de fichier invalide. Seuls les fichiers PDF sont autorisés",
    error_invalid_csv: "Format de fichier invalide. Seuls les fichiers CSV sont autorisés",
    error_missing_email: "Email manquant",
    error_already_exists: "Existe déjà",
    status_pending: "En attente",
    status_uploading: "Importation...",
    status_success: "Succès",
    status_error: "Échec",
    import_complete: "Importation terminée",
    download_template: "Télécharger le modèle CSV",
    csv_columns: "Requis : email. Optionnel : firstname, lastname, phone, city, country, linkedin",
  },
};

export default function ImportCandidateModal({ isOpen, onClose, jobId = null, jobTitle = null, onSuccess }) {
  const { t } = useComponentTranslation(translations);
  const [activeTab, setActiveTab] = useState("resume"); // "resume" | "csv"
  const [files, setFiles] = useState([]);
  const [csvCandidates, setCsvCandidates] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const fileInputRef = useRef(null);
  const csvInputRef = useRef(null);

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const droppedFiles = Array.from(e.dataTransfer.files);
    if (droppedFiles.length > 0) {
      if (activeTab === "resume") {
        validateAndAddPdfFiles(droppedFiles);
      } else {
        validateAndAddCsvFile(droppedFiles[0]);
      }
    }
  };

  const handleFileSelect = (e) => {
    const selectedFiles = Array.from(e.target.files);
    if (selectedFiles.length > 0) {
      validateAndAddPdfFiles(selectedFiles);
    }
    e.target.value = "";
  };

  const handleCsvSelect = (e) => {
    const selectedFiles = Array.from(e.target.files);
    if (selectedFiles.length > 0) {
      validateAndAddCsvFile(selectedFiles[0]);
    }
    e.target.value = "";
  };

  const validateAndAddPdfFiles = (selectedFiles) => {
    const validFiles = [];

    for (const file of selectedFiles) {
      if (!file.type.includes("pdf")) {
        toast.error(`${file.name}: ${t("error_invalid_format")}`);
        continue;
      }

      if (files.some((f) => f.file.name === file.name && f.file.size === file.size)) {
        continue;
      }

      validFiles.push({
        id: `${file.name}-${file.size}-${Date.now()}`,
        file,
        status: "pending",
        error: null,
      });
    }

    if (validFiles.length > 0) {
      setFiles((prev) => [...prev, ...validFiles]);
    }
  };

  const validateAndAddCsvFile = (file) => {
    const isCSV = file.type === "text/csv" || file.name.endsWith(".csv");
    if (!isCSV) {
      toast.error(t("error_invalid_csv"));
      return;
    }

    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        let unmappedCountries = 0;
        const candidates = results.data.map((row, index) => {
          // Normalize column names (handle different capitalizations)
          const normalizedRow = {};
          Object.keys(row).forEach((key) => {
            normalizedRow[key.toLowerCase().trim()] = row[key]?.trim() || "";
          });

          // Ingestion edge: map free-text country to an ISO code, drop what we can't resolve.
          const rawCountry = normalizedRow.country || normalizedRow.pays || "";
          const country = rawCountry ? normalizeCountry(rawCountry) || "" : "";
          if (rawCountry && !country) unmappedCountries++;

          return {
            id: `csv-${index}-${Date.now()}`,
            email: normalizedRow.email || normalizedRow.e_mail || normalizedRow["e-mail"] || "",
            firstname: normalizedRow.firstname || normalizedRow.first_name || normalizedRow.prenom || normalizedRow.prénom || "",
            lastname: normalizedRow.lastname || normalizedRow.last_name || normalizedRow.nom || "",
            phone: normalizedRow.phone || normalizedRow.telephone || normalizedRow.téléphone || normalizedRow.tel || "",
            city: normalizedRow.city || normalizedRow.ville || "",
            country,
            linkedin: normalizedRow.linkedin || normalizedRow.linkedin_url || "",
            resume_url: normalizedRow.resume_url || normalizedRow.resume_url || "",
            status: "pending",
            error: null,
          };
        });

        if (unmappedCountries > 0) {
          toast.error(`${unmappedCountries} row(s) had an unrecognized country — imported without one`);
        }

        // Filter out rows without email
        const validCandidates = candidates.filter((c) => c.email);
        const invalidCount = candidates.length - validCandidates.length;

        if (invalidCount > 0) {
          toast.error(`${invalidCount} rows skipped (missing email)`);
        }

        if (validCandidates.length === 0) {
          toast.error(t("error_no_candidates"));
          return;
        }

        setCsvCandidates(validCandidates);
        toast.success(`${validCandidates.length} ${t("candidates_found")}`);
      },
      error: (error) => {
        console.error("CSV parsing error:", error);
        toast.error(t("error_invalid_csv"));
      },
    });
  };

  const handleRemoveFile = (fileId) => {
    setFiles((prev) => prev.filter((f) => f.id !== fileId));
  };

  const handleRemoveCandidate = (candidateId) => {
    setCsvCandidates((prev) => prev.filter((c) => c.id !== candidateId));
  };

  const handleImportResumes = async () => {
    if (files.length === 0) {
      toast.error(t("error_no_files"));
      return;
    }

    setIsImporting(true);
    let successCount = 0;
    let errorCount = 0;

    for (const fileItem of files) {
      if (fileItem.status !== "pending") continue;

      setFiles((prev) => prev.map((f) => (f.id === fileItem.id ? { ...f, status: "uploading" } : f)));

      try {
        const base64 = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result);
          reader.onerror = reject;
          reader.readAsDataURL(fileItem.file);
        });

        const body = {
          file: base64,
          name: fileItem.file.name,
        };

        if (jobId) body.job_id = jobId;

        const { ok } = await api.post("/user", body);

        if (ok) {
          setFiles((prev) => prev.map((f) => (f.id === fileItem.id ? { ...f, status: "success" } : f)));
          successCount++;
        } else {
          setFiles((prev) => prev.map((f) => (f.id === fileItem.id ? { ...f, status: "error", error: t("error") } : f)));
          errorCount++;
        }
      } catch (error) {
        console.error(error);
        setFiles((prev) => prev.map((f) => (f.id === fileItem.id ? { ...f, status: "error", error: error.message || t("error") } : f)));
        errorCount++;
      }
    }

    setIsImporting(false);

    if (successCount > 0) {
      toast.success(`${successCount} ${t("success")}`);
      if (onSuccess) onSuccess();
    }

    if (errorCount > 0) {
      toast.error(`${errorCount} ${t("error")}`);
    }

    if (errorCount === 0 && successCount > 0) {
      setTimeout(() => handleClose(), 1500);
    }
  };

  const handleImportCsv = async () => {
    if (csvCandidates.length === 0) {
      toast.error(t("error_no_candidates"));
      return;
    }

    setIsImporting(true);
    let successCount = 0;
    let errorCount = 0;

    for (const candidate of csvCandidates) {
      if (candidate.status !== "pending") continue;

      setCsvCandidates((prev) => prev.map((c) => (c.id === candidate.id ? { ...c, status: "uploading" } : c)));

      try {
        const body = {
          email: candidate.email,
          firstname: candidate.firstname,
          lastname: candidate.lastname,
          phone: candidate.phone,
          city: candidate.city,
          country: candidate.country,
          linkedin: candidate.linkedin,
          resume_url: candidate.resume_url,
        };

        if (jobId) body.job_id = jobId;

        const { ok, code } = await api.post("/user", body);

        if (ok) {
          setCsvCandidates((prev) => prev.map((c) => (c.id === candidate.id ? { ...c, status: "success" } : c)));
          successCount++;
        } else {
          const errorMsg = code === "USER_ALREADY_EXISTS" ? t("error_already_exists") : t("error");
          setCsvCandidates((prev) => prev.map((c) => (c.id === candidate.id ? { ...c, status: "error", error: errorMsg } : c)));
          errorCount++;
        }
      } catch (error) {
        console.error(error);
        setCsvCandidates((prev) => prev.map((c) => (c.id === candidate.id ? { ...c, status: "error", error: error.message || t("error") } : c)));
        errorCount++;
      }
    }

    setIsImporting(false);

    if (successCount > 0) {
      toast.success(`${successCount} ${t("success")}`);
      if (onSuccess) onSuccess();
    }

    if (errorCount > 0) {
      toast.error(`${errorCount} ${t("error")}`);
    }

    if (errorCount === 0 && successCount > 0) {
      setTimeout(() => handleClose(), 1500);
    }
  };

  const handleImport = () => {
    if (activeTab === "resume") {
      handleImportResumes();
    } else {
      handleImportCsv();
    }
  };

  const handleClose = () => {
    if (!isImporting) {
      setFiles([]);
      setCsvCandidates([]);
      setActiveTab("resume");
      onClose();
    }
  };

  const downloadCsvTemplate = () => {
    const csvContent =
      "firstname,lastname,email,phone,city,country,linkedin\nJohn,Doe,john.doe@example.com,+33612345678,Paris,France,https://linkedin.com/in/johndoe\nJane,Smith,jane.smith@example.com,+33698765432,Lyon,France,";
    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "candidates_template.csv";
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const pendingCount = activeTab === "resume" ? files.filter((f) => f.status === "pending").length : csvCandidates.filter((c) => c.status === "pending").length;

  const successCount = activeTab === "resume" ? files.filter((f) => f.status === "success").length : csvCandidates.filter((c) => c.status === "success").length;

  const errorCountDisplay = activeTab === "resume" ? files.filter((f) => f.status === "error").length : csvCandidates.filter((c) => c.status === "error").length;

  const totalItems = activeTab === "resume" ? files.length : csvCandidates.length;

  return (
    <Modal isOpen={isOpen} onClose={handleClose} className="w-full max-w-lg">
      <div className="bg-white rounded-2xl shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div>
            <h2 className="text-xl font-bold text-gray-900">{jobId && jobTitle ? t("title_with_job") : t("title")}</h2>
            {jobId && jobTitle && <p className="text-sm text-indigo-600 font-medium mt-0.5">{jobTitle}</p>}
          </div>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-200 px-6">
          <button
            onClick={() => !isImporting && setActiveTab("resume")}
            disabled={isImporting}
            className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === "resume" ? "border-indigo-600 text-indigo-600" : "border-transparent text-gray-500 hover:text-gray-700"
            } ${isImporting ? "cursor-not-allowed opacity-50" : ""}`}
          >
            <div className="flex items-center gap-2">
              <FiFile className="w-4 h-4" />
              {t("tab_resume")}
            </div>
          </button>
          <button
            onClick={() => !isImporting && setActiveTab("csv")}
            disabled={isImporting}
            className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === "csv" ? "border-indigo-600 text-indigo-600" : "border-transparent text-gray-500 hover:text-gray-700"
            } ${isImporting ? "cursor-not-allowed opacity-50" : ""}`}
          >
            <div className="flex items-center gap-2">
              <FiTable className="w-4 h-4" />
              {t("tab_csv")}
            </div>
          </button>
        </div>

        {/* Content */}
        <div className="px-6 py-6">
          {activeTab === "resume" ? (
            <>
              <p className="text-sm text-gray-600 mb-4">{jobId ? t("description_resume_with_job") : t("description_resume")}</p>

              {/* PDF Upload Area */}
              <div
                onDragEnter={handleDragEnter}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
                className={`border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-all ${
                  isDragging ? "border-indigo-500 bg-indigo-50" : "border-gray-300 hover:border-indigo-400 hover:bg-gray-50"
                }`}
              >
                <div className="flex flex-col items-center gap-2">
                  <div className={`p-3 rounded-full ${isDragging ? "bg-indigo-100" : "bg-gray-100"}`}>
                    <FiUpload className={`w-6 h-6 ${isDragging ? "text-indigo-600" : "text-gray-600"}`} />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-900">{t("drag_drop")}</p>
                    <p className="text-xs text-gray-500 mt-1">{t("drag_drop_multiple")}</p>
                    <p className="text-xs text-gray-500 mt-1">{t("supported_formats")}</p>
                  </div>
                </div>
                <input ref={fileInputRef} type="file" accept=".pdf" multiple onChange={handleFileSelect} className="hidden" />
              </div>

              {/* PDF Files List */}
              {files.length > 0 && (
                <div className="mt-4 space-y-2 max-h-64 overflow-y-auto">
                  <p className="text-sm font-medium text-gray-700 mb-2">
                    {files.length} {t("files_selected")}
                  </p>
                  {files.map((fileItem) => (
                    <FileRow key={fileItem.id} item={fileItem} onRemove={handleRemoveFile} isImporting={isImporting} t={t} type="file" />
                  ))}
                </div>
              )}
            </>
          ) : (
            <>
              <p className="text-sm text-gray-600 mb-2">{jobId ? t("description_csv_with_job") : t("description_csv")}</p>
              <p className="text-xs text-gray-500 mb-4">{t("csv_columns")}</p>

              {/* Download Template Button */}
              <button onClick={downloadCsvTemplate} className="flex items-center gap-2 text-sm text-indigo-600 hover:text-indigo-700 mb-4">
                <FiDownload className="w-4 h-4" />
                {t("download_template")}
              </button>

              {/* CSV Upload Area */}
              <div
                onDragEnter={handleDragEnter}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onClick={() => csvInputRef.current?.click()}
                className={`border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-all ${
                  isDragging ? "border-indigo-500 bg-indigo-50" : "border-gray-300 hover:border-indigo-400 hover:bg-gray-50"
                }`}
              >
                <div className="flex flex-col items-center gap-2">
                  <div className={`p-3 rounded-full ${isDragging ? "bg-indigo-100" : "bg-gray-100"}`}>
                    <FiTable className={`w-6 h-6 ${isDragging ? "text-indigo-600" : "text-gray-600"}`} />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-900">{t("drag_drop_csv")}</p>
                    <p className="text-xs text-gray-500 mt-1">{t("supported_formats_csv")}</p>
                  </div>
                </div>
                <input ref={csvInputRef} type="file" accept=".csv" onChange={handleCsvSelect} className="hidden" />
              </div>

              {/* CSV Candidates List */}
              {csvCandidates.length > 0 && (
                <div className="mt-4 space-y-2 max-h-64 overflow-y-auto">
                  <p className="text-sm font-medium text-gray-700 mb-2">
                    {csvCandidates.length} {t("candidates_found")}
                  </p>
                  {csvCandidates.map((candidate) => (
                    <FileRow key={candidate.id} item={candidate} onRemove={handleRemoveCandidate} isImporting={isImporting} t={t} type="candidate" />
                  ))}
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between gap-3 px-6 py-4 bg-gray-50 border-t border-gray-200">
          <div className="text-sm text-gray-600">
            {totalItems > 0 && (
              <span>
                {pendingCount} pending
                {successCount > 0 && `, ${successCount} imported`}
                {errorCountDisplay > 0 && `, ${errorCountDisplay} failed`}
              </span>
            )}
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={handleClose}
              disabled={isImporting}
              className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-200 rounded-lg transition-colors disabled:opacity-50"
            >
              {t("cancel")}
            </button>
            <button
              onClick={handleImport}
              disabled={totalItems === 0 || isImporting || pendingCount === 0}
              className="px-6 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {isImporting ? (
                <>
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  <span>{t("importing")}</span>
                </>
              ) : (
                <>
                  <FiUpload className="w-4 h-4" />
                  <span>
                    {t("import")} ({pendingCount})
                  </span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </Modal>
  );
}

function FileRow({ item, onRemove, isImporting, t, type }) {
  const displayName = type === "file" ? item.file.name : `${item.firstname || ""} ${item.lastname || ""}`.trim() || item.email;

  const displaySubtext = type === "file" ? `${(item.file.size / 1024 / 1024).toFixed(2)} MB` : item.email;

  return (
    <div
      className={`flex items-center gap-3 p-3 rounded-lg border ${
        item.status === "success"
          ? "bg-emerald-50 border-emerald-200"
          : item.status === "error"
            ? "bg-red-50 border-red-200"
            : item.status === "uploading"
              ? "bg-blue-50 border-blue-200"
              : "bg-gray-50 border-gray-200"
      }`}
    >
      <div
        className={`p-2 rounded-lg ${
          item.status === "success" ? "bg-emerald-100" : item.status === "error" ? "bg-red-100" : item.status === "uploading" ? "bg-blue-100" : "bg-gray-100"
        }`}
      >
        {item.status === "uploading" && <FiLoader className="w-4 h-4 text-blue-600 animate-spin" />}
        {item.status === "success" && <FiCheck className="w-4 h-4 text-emerald-600" />}
        {item.status === "error" && <FiAlertCircle className="w-4 h-4 text-red-600" />}
        {item.status === "pending" && (type === "file" ? <FiFile className="w-4 h-4 text-gray-600" /> : <FiTable className="w-4 h-4 text-gray-600" />)}
      </div>

      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-gray-900 truncate">{displayName}</p>
        <div className="flex items-center gap-2 mt-0.5">
          <p className="text-xs text-gray-500 truncate">{displaySubtext}</p>
          <span className="text-xs text-gray-400">•</span>
          <p
            className={`text-xs font-medium ${
              item.status === "success" ? "text-emerald-600" : item.status === "error" ? "text-red-600" : item.status === "uploading" ? "text-blue-600" : "text-gray-500"
            }`}
          >
            {item.status === "pending" && t("status_pending")}
            {item.status === "uploading" && t("status_uploading")}
            {item.status === "success" && t("status_success")}
            {item.status === "error" && t("status_error")}
          </p>
        </div>
        {item.error && <p className="text-xs text-red-600 mt-1">{item.error}</p>}
      </div>

      {item.status === "pending" && !isImporting && (
        <button onClick={() => onRemove(item.id)} className="p-1.5 hover:bg-gray-200 rounded transition-colors" title={t("remove")}>
          <FiX className="w-4 h-4 text-gray-600" />
        </button>
      )}
    </div>
  );
}
